import { toBerlinISODate } from "components/Calendar";

export const SLOT_HEIGHT_PX = 72;
export const SLOT_MINUTES = 30;

const SCHEDULES = {
  "tawan-spa": { openHour: 10, closeHour: 22, closedWeekdays: [2] },
  "dr-hoffmann": { openHour: 10, closeHour: 22, closedWeekdays: [0, 6] },
  all: { openHour: 10, closeHour: 22, closedWeekdays: [] },
};

export const resolveSchedule = (brand = "tawan-spa") =>
  SCHEDULES[brand] || SCHEDULES["tawan-spa"];

export const buildTimeSlots = (openHour, closeHour, stepMinutes = SLOT_MINUTES) => {
  const slots = [];
  for (let hour = openHour; hour < closeHour; hour += 1) {
    for (let minute = 0; minute < 60; minute += stepMinutes) {
      if (hour === closeHour - 1 && minute >= 60) break;
      slots.push({
        hour,
        minute,
        label: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
      });
    }
  }
  return slots;
};

const getBerlinWeekday = (date) => {
  const short = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Berlin",
    weekday: "short",
  }).format(new Date(date));
  const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return map[short] ?? new Date(date).getDay();
};

export const getBerlinTimeParts = (date) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Berlin",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  }).formatToParts(new Date(date));
  let hour = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
  if (hour === 24) hour = 0;
  return { hour, minute };
};

export const isDayClosed = (date, scheduleBrand) => {
  const schedule = resolveSchedule(scheduleBrand);
  return schedule.closedWeekdays.includes(getBerlinWeekday(date));
};

export const bookingPlacement = (booking, openHour, slotHeightPx = SLOT_HEIGHT_PX) => {
  const { hour, minute } = getBerlinTimeParts(booking.start);
  const minutesFromOpen = Math.max(0, (hour - openHour) * 60 + minute);
  const top = (minutesFromOpen / SLOT_MINUTES) * slotHeightPx;
  const height = Math.max(
    slotHeightPx * 0.85,
    (booking.durationMinutes / SLOT_MINUTES) * slotHeightPx
  );
  return { top, height };
};

const bookingStartMs = (booking) => new Date(booking.start).getTime();
const bookingEndMs = (booking) => new Date(booking.end).getTime();

const bookingsOverlap = (a, b) =>
  bookingStartMs(a) < bookingEndMs(b) && bookingStartMs(b) < bookingEndMs(a);

const buildOverlapClusters = (bookings) => {
  const sorted = [...bookings].sort((a, b) => bookingStartMs(a) - bookingStartMs(b));
  const clusters = [];

  for (const booking of sorted) {
    const matching = clusters.filter((cluster) =>
      cluster.some((other) => bookingsOverlap(other, booking))
    );
    if (matching.length === 0) {
      clusters.push([booking]);
      continue;
    }
    const merged = matching.flat();
    merged.push(booking);
    for (const cluster of matching) {
      clusters.splice(clusters.indexOf(cluster), 1);
    }
    clusters.push(merged);
  }

  return clusters;
};

const assignOverlapColumns = (cluster) => {
  const sorted = [...cluster].sort((a, b) => bookingStartMs(a) - bookingStartMs(b));
  const columnEnds = [];
  const columnById = new Map();

  for (const booking of sorted) {
    const start = bookingStartMs(booking);
    const end = bookingEndMs(booking);
    let column = columnEnds.findIndex((colEnd) => colEnd <= start);
    if (column === -1) {
      column = columnEnds.length;
      columnEnds.push(end);
    } else {
      columnEnds[column] = end;
    }
    columnById.set(String(booking._id), column);
  }

  const totalColumns = Math.max(1, columnEnds.length);
  const layout = new Map();
  for (const booking of cluster) {
    layout.set(String(booking._id), {
      column: columnById.get(String(booking._id)) ?? 0,
      totalColumns,
    });
  }
  return layout;
};

/** Column index + lane count for concurrent bookings on the same day. */
export const computeBookingOverlapLayout = (bookings) => {
  const result = new Map();
  if (!bookings?.length) return result;

  for (const cluster of buildOverlapClusters(bookings)) {
    const clusterLayout = assignOverlapColumns(cluster);
    clusterLayout.forEach((value, id) => result.set(id, value));
  }
  return result;
};

export const layoutDayBookings = (bookings, openHour, slotHeightPx = SLOT_HEIGHT_PX) => {
  const overlap = computeBookingOverlapLayout(bookings);
  return [...bookings]
    .sort((a, b) => bookingStartMs(a) - bookingStartMs(b))
    .map((booking) => {
      const id = String(booking._id);
      const { top, height } = bookingPlacement(booking, openHour, slotHeightPx);
      const { column = 0, totalColumns = 1 } = overlap.get(id) || {};
      return { booking, top, height, column, totalColumns };
    });
};

export const eventLaneStyle = (column, totalColumns) => {
  const inset = 3;
  if (totalColumns <= 1) {
    return { left: inset, right: inset };
  }
  const gap = 2;
  const widthPct = 100 / totalColumns;
  return {
    left: `calc(${column * widthPct}% + ${gap / 2}px)`,
    width: `calc(${widthPct}% - ${gap}px)`,
    right: "auto",
  };
};

export const formatDayHeader = (date, dateLocale, variant = "week") => {
  if (variant === "day") {
    return {
      primary: date.toLocaleDateString(dateLocale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "Europe/Berlin",
      }),
      secondary: null,
    };
  }
  return {
    primary: date.toLocaleDateString(dateLocale, {
      weekday: "short",
      timeZone: "Europe/Berlin",
    }),
    secondary: date.toLocaleDateString(dateLocale, {
      day: "numeric",
      month: "short",
      timeZone: "Europe/Berlin",
    }),
  };
};

export const dayKey = (date) => toBerlinISODate(date);
